import { CheckCircle2, MailCheck, XCircle } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, FormField, Input } from '@/components/ui';
import { BrandMark } from '@/components/Brand';
import { brand } from '@/config/brand';
import { useAuth } from '@/hooks/useAuth';
import { useNotify } from '@/hooks/useNotify';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';
import { paths } from '@/router/paths';

/**
 * Oeffentliche Seiten rund um die Anmeldung: Passwort vergeben, Passwort
 * zuruecksetzen und Adressbestaetigung. Sie sind auch ohne Sitzung erreichbar,
 * weil sie die Ziele der Links aus den Mails sind.
 */

function AuthShell({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  const { t } = useI18n();
  // Diese Seiten sind in beiden Zustaenden erreichbar - wer angemeldet ist,
  // soll nicht zur Anmeldung zurueckgeschickt werden.
  const { user } = useAuth();

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-bg-0 px-4 py-10">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(70% 55% at 50% -10%, var(--acc-soft), transparent 70%), radial-gradient(50% 40% at 90% 100%, var(--info-soft), transparent 70%)',
        }}
        aria-hidden
      />

      <div className="relative w-full max-w-[420px]">
        <div className="mb-7 flex flex-col items-center gap-3.5 text-center">
          <BrandMark size={52} />
          <div>
            <h1 className="text-[22px] font-extrabold text-t0">{brand.name}</h1>
            <p className="mt-1 text-[13px] text-t1">{title}</p>
          </div>
        </div>

        <div className="rounded-[var(--radius-vela-xl)] border border-line bg-bg-2 p-6 shadow-[var(--shadow-vela)]">
          {subtitle && <p className="mb-4 text-[13px] leading-relaxed text-t1">{subtitle}</p>}
          {children}
        </div>

        <p className="mt-5 text-center text-[13px]">
          <Link to={user ? paths.dashboard : paths.login} className="text-acc hover:underline">
            {user ? t('nav.dashboard') : t('auth.backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function PasswordForgotPage() {
  const { t } = useI18n();
  const notify = useNotify();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.auth.forgotPassword(email);
      // Bewusst dieselbe Meldung, egal ob das Konto existiert.
      setSent(true);
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t('auth.forgotTitle')} subtitle={sent ? undefined : t('auth.forgotSubtitle')}>
      {sent ? (
        <div className="flex items-start gap-3">
          <MailCheck size={20} className="mt-0.5 shrink-0 text-ok" />
          <p className="text-[13px] leading-relaxed text-t1">{t('auth.forgotSent')}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <FormField label={t('auth.email')} required>
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </FormField>
          <Button type="submit" fullWidth disabled={busy || !email}>
            {t('auth.forgotSubmit')}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

// ---------------------------------------------------------------------------

export function PasswordResetPage() {
  const { t } = useI18n();
  const notify = useNotify();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);

  const tooShort = password.length > 0 && password.length < 10;
  const mismatch = repeat.length > 0 && password !== repeat;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (tooShort || mismatch) return;

    setBusy(true);
    try {
      await api.auth.resetPassword(token, password);
      notify.success(t('auth.resetSuccess'));
      // Der Server hat bereits eine Sitzung gesetzt - Zustand nachziehen.
      await refresh();
    } catch (cause) {
      notify.fromError(cause, t('auth.resetInvalid'));
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title={t('auth.resetTitle')}>
        <div className="flex items-start gap-3">
          <XCircle size={20} className="mt-0.5 shrink-0 text-bad" />
          <p className="text-[13px] leading-relaxed text-t1">{t('auth.resetInvalid')}</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('auth.resetTitle')} subtitle={t('auth.resetSubtitle')}>
      <form onSubmit={submit} className="space-y-4">
        <FormField
          label={t('auth.newPassword')}
          required
          error={tooShort ? t('auth.passwordTooShort') : undefined}
        >
          <Input
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </FormField>

        <FormField
          label={t('auth.repeatPassword')}
          required
          error={mismatch ? t('auth.passwordMismatch') : undefined}
        >
          <Input
            type="password"
            autoComplete="new-password"
            required
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
          />
        </FormField>

        <Button type="submit" fullWidth disabled={busy || tooShort || mismatch || !password}>
          {t('auth.resetSubmit')}
        </Button>
      </form>
    </AuthShell>
  );
}

// ---------------------------------------------------------------------------

export function VerifyEmailPage() {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [state, setState] = useState<'pending' | 'ok' | 'failed'>('pending');

  useEffect(() => {
    if (!token) {
      setState('failed');
      return;
    }
    void (async () => {
      try {
        await api.auth.verifyEmail(token);
        setState('ok');
        // Meldet zugleich an - der Server setzt die Sitzung mit.
        await refresh();
      } catch {
        setState('failed');
      }
    })();
  }, [token, refresh]);

  return (
    <AuthShell title={t('auth.verifyTitle')}>
      <div className="flex items-start gap-3">
        {state === 'pending' && <p className="text-[13px] text-t1">{t('auth.verifyPending')}</p>}
        {state === 'ok' && (
          <>
            <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-ok" />
            <p className="text-[13px] leading-relaxed text-t1">{t('auth.verifySuccess')}</p>
          </>
        )}
        {state === 'failed' && (
          <>
            <XCircle size={20} className="mt-0.5 shrink-0 text-bad" />
            <p className="text-[13px] leading-relaxed text-t1">{t('auth.verifyInvalid')}</p>
          </>
        )}
      </div>
    </AuthShell>
  );
}

/** Hinweisstreifen im Panel, solange die Adresse unbestaetigt ist. */
export function VerifyEmailBanner() {
  const { t } = useI18n();
  const notify = useNotify();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user || user.emailVerified) return null;

  const resend = async () => {
    setBusy(true);
    try {
      const result = await api.auth.resendVerification();
      notify.success(result.alreadyVerified ? t('auth.verifyAlready') : t('auth.verifyResent'));
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-[var(--radius-vela-md)] border border-warn/30 bg-warn-soft px-4 py-3">
      <MailCheck size={17} className="shrink-0 text-warn" />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-t0">{t('auth.verifyBannerTitle')}</p>
        <p className="text-[12px] text-t1">{t('auth.verifyBannerText')}</p>
      </div>
      <Button size="sm" variant="outline" onClick={() => void resend()} disabled={busy}>
        {t('auth.verifyResend')}
      </Button>
    </div>
  );
}
